import { createContext, useContext, useEffect, useState } from "react";
import { setSession } from "../utils/auth.js";
import * as API from '../services/api'
import localStorageAvailable from "../utils/localStorageAvailable.js";
import { AnalyticsService, EVENT_NAMES } from "@/services/analytics.js";
import { AUTH_BASE_PATH } from '@/consts/index.js'
import Hotjar from '@hotjar/browser'
import { HOTJAR_SITE_ID } from "@/config-global.jsx";
// import Intercom from '@intercom/messenger-js-sdk'

export const AuthContext = createContext(null);


export function AuthProvider({ children }) {

    const [user, setUser] = useState(null)
    const [accounts, setAccounts] = useState([])
    const [spaces, setSpaces] = useState([])
    const [spaceUsersMap, setSpaceUsersMap] = useState({})
    const [accountUsersMap, setAccountUsersMap] = useState({})

    const [isInitialized, setIsInitialized] = useState(false)
    const queryParams = new URLSearchParams(window.location.search)
    const avoidAuthenticateOnInit = queryParams.get('customFlow') // Meaning we intend to override the default behavior of the auth flow. we do it for colab team to be able to log in behalf of users.

    const getAuthEntitiesAndSetSession = async (userData) => {

        const [accountsOfUserResponse, spacesOfUserResponse] = await Promise.all([
            API.getUserAccountsByJwt(userData.jwt),
            API.getSpacesByJwt(userData.jwt)
        ])

        // an 'Orphan' user means the user has no accounts or spaces - it could happen if the user has been deleted from a space and not assign to any account
        const isOrphanUser = !accountsOfUserResponse.data?.length && spacesOfUserResponse.data?.length
        if (isOrphanUser) {
            // TODO - implement this. need to display a form to register new account
            alert('You have been removed from all accounts and spaces. Please contact your team to add you back.')
            logout()
            return
        }

        // Set the spaces and accounts to local state
        setSpaces(spacesOfUserResponse.data)
        setAccounts(accountsOfUserResponse.data)
        // Set session
        setSession(userData.jwt)
        setUser(userData)
        setIsInitialized(true)
    }

    const authenticate = async (userData) => {
        try {
            if (userData) {
                await getAuthEntitiesAndSetSession(userData)
                return
            }

            // Continue auth flow
            if (!localStorageAvailable()) {
                setIsInitialized(true)
                return
            }

            const jwt = localStorage.getItem('jwt')
            if (!jwt) {
                setIsInitialized(true)
                return
            }

            const { data } = await API.getUser(jwt)

            if (!data) {
                localStorage.removeItem('jwt')
                setIsInitialized(true)
                return
            }

            await getAuthEntitiesAndSetSession(data)

        } catch (e) {
            console.log(e)
            setSession(null)
            alert(e.message)
            window.location.replace(AUTH_BASE_PATH)
        }
    }

    const logout = () => {
        AnalyticsService.track(EVENT_NAMES.LOGOUT)
        setSession()
        window.location.href = window.location.origin + AUTH_BASE_PATH
    }

    /* When the user is authenticated, we want to send the user and account details to the analytics platforms */
    useEffect(() => {
        if (user) {
            const account = accounts?.[0]
            const userData = {
                ...user,
                name: user.shopName ? user.shopName : user.name,
                // We want to send the account details to the analytics platforms
                account: {
                    _id: account?._id,
                    name: account?.name,
                    type: account?.type,
                    status: account?.status,
                    createdAt: account?.createdAt,
                    updatedAt: account?.updatedAt,
                    mainWebsite: account?.mainWebsite,
                }
            }
            delete userData.jwt // Don't send this to the analytics platforms...
            delete userData.profileImage // Don't send this to the analytics platforms, too much data
            delete userData.password // Don't send this to the analytics platforms, too much data

            // For the User
            AnalyticsService.identify(user._id, userData)
            // For the Account
            if (account) {
                const accountDataToSend = { ...account }
                delete accountDataToSend.users // the payload is too big, we don't need to send this to the analytics platforms
                AnalyticsService.group(account._id, accountDataToSend)
            }

            // Hotjar identify
            try {
                if (HOTJAR_SITE_ID) {
                    Hotjar.identify(user._id, userData)
                }
            } catch (e) {
                console.error(e)
            }

            // Currently disable intercom
            // try {
            //     Intercom({
            //         app_id: 'yz0ekdp5',
            //         user_id: user._id,
            //         name: user.name,
            //         email: user.email,
            //         created_at: user.createdAt,
            //         hide_default_launcher: true
            //     })
            // } catch (e) {
            //     console.error(e)
            // }
        }
    }, [user?._id])

    // Use this when user manually change thier details
    const updateUserData = async ({ userName, userEmail, profileImage }) => {
        setUser({ ...user, name: userName, email: userEmail, profileImage })
    }

    // Do this after the user invite a member
    const updateAccountInvitees = (accountId, newInvitees) => {
        setAccounts(accounts.map(account => {
            if (account._id === accountId) {
                account.users = [...account.users, newInvitees]
            }
            return account
        }))
    }

    // Do this after the user update their account details
    const updateAccountDetails = async (accountId, { name, email, shouldInitializeResearch }) => {
        setAccounts(accounts.map(account => {
            if (account._id === accountId) {
                return { ...account, name, email, shouldInitializeResearch }
            }
            return account
        }))
    }

    // Update space on local state
    const updateSpaceState = async (spaceId, data) => {
        setSpaces(spaces.map(space => {
            if (space._id === spaceId) {
                return data
            }
            return space
        }))
    }

    const addSpaceToState = (space) => {
        setSpaces(prev => [...prev, space])
    }

    const removeSpaceFromState = (spaceId) => {
        setSpaces(spaces.filter(space => space._id !== spaceId))
    }

    const setManySpaceUsers = (spaceIdsWithUsers) => {
        const newSpaceUsersMap = JSON.parse(JSON.stringify(spaceUsersMap))
        spaceIdsWithUsers.forEach(space => {
            newSpaceUsersMap[space.spaceId] = space.users
        })
        setSpaceUsersMap(newSpaceUsersMap)
    }

    // Set users for each space
    const setSpaceUsers = (spaceId, users) => {
        setSpaceUsersMap(prev => ({ ...prev, [spaceId]: users }))
    }

    // get users of space
    const getSpaceUsers = (spaceId) => {
        return spaceUsersMap[spaceId] || []
    }

    // Set users for each account
    const setAccountUsers = (accountId, users) => {
        setAccountUsersMap(prev => ({ ...prev, [accountId]: users }))
    }

    // get users of account
    const getAccountUsers = (accountId) => {
        return accountUsersMap[accountId] || []
    }

    // Call to 'authenticate' on the first render
    useEffect(() => {
        if (!avoidAuthenticateOnInit) {
            authenticate()
        }
    }, [])

    return (
        <AuthContext.Provider
            value={{
                user,
                isInitialized,
                accounts,
                setAccounts,
                setAccountUsers,
                getAccountUsers,
                addSpaceToState,
                removeSpaceFromState,
                updateAccountDetails,
                updateAccountInvitees,
                updateUserData,
                updateSpaceState,
                authenticate,
                setSpaceUsers,
                getSpaceUsers,
                setManySpaceUsers,
                spaces,
                logout
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}


export const useAuthContext = () => {
    const context = useContext(AuthContext);

    if (!context) throw new Error('useAuthContext context must be use inside AuthProvider');

    return context;
};
